import { memo, useMemo, useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { QueryField, QueryRule } from '../../../../../types/QueryBuilder';
import { primarySoft } from '../../../../../theme';
import { useLanguage } from '../../../../../context/LanguageContext';
import { isRuleValid } from '../../../../../utils/queryBuilderUtils';
import { groupFieldsBySubcategory } from './fieldConfig';
import { buildSummaryField, HIDDEN_FROM_SUMMARY_FIELD_NAMES, SummaryField } from './summaryBuilders';

interface SelectionSummaryProps {
    fields: QueryField[];
    rules: QueryRule[];
}

const COLLAPSED_VALUES_LIMIT = 6;

function ValueList({ values }: { values: string[] }) {
    const { t } = useLanguage();
    const [expanded, setExpanded] = useState(false);
    const isCollapsible = values.length > COLLAPSED_VALUES_LIMIT;
    const visibleValues = expanded || !isCollapsible ? values : values.slice(0, COLLAPSED_VALUES_LIMIT);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 0.25, pl: 2 }}>
            {visibleValues.map((value, index) => (
                <Typography key={index} variant="body2" sx={{ color: 'text.primary' }}>
                    {value}
                </Typography>
            ))}
            {isCollapsible && (
                <Button
                    size="small"
                    onClick={() => setExpanded(v => !v)}
                    sx={{ textTransform: 'none', fontSize: '0.78rem', color: 'primary.dark', p: 0, minWidth: 0, justifyContent: 'flex-start', '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}
                >
                    {expanded
                        ? t('pages.clients.queryBuilder.simple.summary.showLess')
                        : t('pages.clients.queryBuilder.simple.summary.showMore', { count: values.length - COLLAPSED_VALUES_LIMIT })}
                </Button>
            )}
        </Box>
    );
}

function SummaryItem({ item }: { item: SummaryField }) {
    return (
        <Box sx={{ mt: 1 }}>
            {item.label && (
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'grey.800' }}>
                    {item.label}
                </Typography>
            )}
            {item.intro && (
                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                    {item.intro}
                </Typography>
            )}
            <ValueList values={item.values} />
            {item.secondIntro && (
                <Typography variant="body2" sx={{ color: 'text.primary', mt: 0.5 }}>
                    {item.secondIntro}
                </Typography>
            )}
            {item.secondValues && <ValueList values={item.secondValues} />}
        </Box>
    );
}

function SelectionSummary({ fields, rules }: SelectionSummaryProps) {
    const { t, formatLocale } = useLanguage();

    const summaryGroups = useMemo(
        () => groupFieldsBySubcategory(fields)
            .map(({ titleKey, fields: groupFields }) => ({
                titleKey,
                items: groupFields
                    .filter(field => !HIDDEN_FROM_SUMMARY_FIELD_NAMES.has(field.name))
                    .map(field => {
                        const rule = rules.find(r => r.field === field.name);
                        return rule && isRuleValid(rule) ? buildSummaryField(field, rule, t, formatLocale, fields, rules) : null;
                    })
                    .filter((item): item is SummaryField => item !== null),
            }))
            .filter(group => group.items.length > 0),
        [fields, rules, t, formatLocale]
    );

    return (
        <Box sx={{ mt: 2, mb: 2, p: 1.5, bgcolor: (t) => primarySoft(t, 0.1), border: '1px solid', borderColor: (t) => primarySoft(t, 0.3), borderRadius: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.dark' }}>
                {t('pages.clients.queryBuilder.simple.summary.title')}
            </Typography>
            {summaryGroups.length === 0 ? (
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                    {t('pages.clients.queryBuilder.simple.summary.empty')}
                </Typography>
            ) : (
                <Box sx={{ mt: 1, columnCount: { xs: 1, md: 2, xl: 3 }, columnGap: '20px' }}>
                    {summaryGroups.map(group => (
                        <Box key={group.titleKey} sx={{ breakInside: 'avoid', WebkitColumnBreakInside: 'avoid', mb: 2.5 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.dark' }}>
                                {t(group.titleKey)}.
                            </Typography>
                            {group.items.map(item => <SummaryItem key={item.id} item={item} />)}
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
}

export default memo(SelectionSummary);
